import { useEffect, useState } from 'react'
import { registro, mensagemDoErro } from '../services/api'
import { Card, Titulo, Tile, Provedor, Etiqueta, Erro, Vazio, dinheiro, numero } from '../components/ui'

/**
 * O que está no ar, e o que de fato rodou.
 *
 * As duas metades juntas na mesma tela de propósito. O registro diz a INTENÇÃO
 * e os eventos dizem a REALIDADE; separadas, cada uma parece coerente. É a
 * diferença entre elas que mostra os problemas caros — e todos são silenciosos:
 * um modelo rodando sem estar declarado não gera erro nenhum, só uma conta
 * maior no fim do mês.
 */
export default function Inventario() {
  const [dias, setDias] = useState(30)
  const [dados, setDados] = useState(null)
  const [erro, setErro] = useState('')

  useEffect(() => {
    let vivo = true
    setErro('')
    Promise.all([registro.console(), registro.portfolio(dias), registro.drift(dias), registro.motores()])
      .then(([c, p, d, m]) => vivo && setDados({ ...c, portfolio: p, drift: d, motores: m.mortos }))
      .catch((e) => vivo && setErro(mensagemDoErro(e)))
    return () => { vivo = false }
  }, [dias])

  if (erro) return <Erro>{erro}</Erro>
  if (!dados) return <Vazio>carregando…</Vazio>

  const conteudo = dados.publicado?.content
  const papeis = Object.entries(conteudo?.roles ?? {})
  const modelos = dados.portfolio.models
  const custoTotal = modelos.reduce((s, m) => s + m.costUsd, 0)
  const semPreco = dados.drift.semPreco.length
  const naoDeclarados = dados.drift.rodandoSemDeclarar.length

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Titulo sub="O registro publicado ao lado do consumo real. A diferença entre os dois é onde estão os problemas que ninguém vê.">
          {dados.publicado
            ? `Edição v${dados.publicado.version} no ar`
            : 'Nenhuma edição publicada'}
        </Titulo>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDias(d)}
              aria-pressed={dias === d}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                dias === d
                  ? 'border-pinho-500 bg-pinho-500 text-white'
                  : 'border-zinc-300 bg-white text-zinc-600 hover:border-zinc-400'
              }`}
            >
              {d} dias
            </button>
          ))}
        </div>
      </div>

      {dados.motores?.length > 0 && (
        <Card className="border-red-400 bg-red-50 p-5">
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-red-800">
            Motor sem resposta
          </h3>
          <p className="mb-3 max-w-[74ch] text-sm text-zinc-700">
            Está falhando praticamente tudo. Não é instabilidade — é o provedor recusando, e a
            causa mais comum é o modelo ter sido aposentado sem aviso. Foi assim que o
            <span className="font-mono"> gemini-2.0-flash </span> ficou desligado por um período
            indeterminado no Hadrians.
          </p>
          <ul className="flex flex-col gap-1.5">
            {dados.motores.map((m) => (
              <li key={`${m.provider}/${m.model}`} className="font-mono text-xs text-red-900">
                {m.provider}/{m.model} — {numero(m.falhas)}/{numero(m.eventos)} falhas
                {' '}({Math.round(m.taxaDeFalha * 100)}%) desde{' '}
                {new Date(m.desde).toLocaleString('pt-BR')}
                <span className="text-red-400"> · {m.products.join(', ')}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile n={`US$ ${dinheiro(custoTotal)}`} rotulo={`custo estimado em ${dias} dias`} />
        <Tile n={modelos.length} rotulo="modelos que de fato rodaram" />
        <Tile n={semPreco} rotulo="rodando sem preço na tabela" alerta />
        <Tile n={naoDeclarados} rotulo="rodando sem estar no registro" alerta />
      </div>

      {(semPreco > 0 || naoDeclarados > 0 || dados.drift.declaradoSemRodar.length > 0) && (
        <Card className="border-amber-300 bg-amber-50/40 p-5">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-amber-800">
            O declarado não bate com o real
          </h3>
          <div className="flex flex-col gap-3 text-sm text-zinc-700">
            {naoDeclarados > 0 && (
              <p>
                <b>Rodando sem estar no registro:</b>{' '}
                <span className="font-mono">
                  {dados.drift.rodandoSemDeclarar.map((m) => m.model).join(', ')}
                </span>
                . Não gera erro nenhum — só uma conta maior.
              </p>
            )}
            {semPreco > 0 && (
              <p>
                <b>Sem preço na tabela:</b>{' '}
                <span className="font-mono">{dados.drift.semPreco.map((m) => m.model).join(', ')}</span>.
                O custo desses eventos entra como zero, e custo subestimado não parece erro: parece
                um mês barato.
              </p>
            )}
            {dados.drift.declaradoSemRodar.length > 0 && (
              <p>
                <b>Declarado e parado:</b>{' '}
                <span className="font-mono">{dados.drift.declaradoSemRodar.join(', ')}</span>. Ou o
                produto ainda não migrou, ou o papel virou letra morta.
              </p>
            )}
          </div>
        </Card>
      )}

      <section className="flex flex-col gap-3">
        <Titulo sub="Ordenado por custo, não por volume: a linha que interessa é a cara, não a frequente.">
          Consumo real
        </Titulo>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                <th className="p-3 font-semibold">Modelo</th>
                <th className="p-3 font-semibold">Provedor</th>
                <th className="p-3 font-semibold">Produtos</th>
                <th className="p-3 text-right font-semibold">Chamadas</th>
                <th className="p-3 text-right font-semibold">Custo US$</th>
                <th className="p-3 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {modelos.map((m) => (
                <tr key={`${m.provider}/${m.model}`} className="border-b border-zinc-100 last:border-0">
                  <td className="p-3 font-mono text-xs">{m.model}</td>
                  <td className="p-3"><Provedor id={m.provider} /></td>
                  <td className="p-3 text-xs text-zinc-500">{m.products.join(', ')}</td>
                  <td className="p-3 text-right font-mono text-xs tabular-nums">{numero(m.events)}</td>
                  <td className="p-3 text-right font-mono text-xs tabular-nums">{dinheiro(m.costUsd)}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {m.unpricedEvents > 0 && <Etiqueta tom="aviso">sem preço</Etiqueta>}
                      {!conteudo?.models?.[m.model] && <Etiqueta tom="perigo">não declarado</Etiqueta>}
                      {m.failures > 0 && <Etiqueta tom="neutro">{numero(m.failures)} falhas</Etiqueta>}
                    </div>
                  </td>
                </tr>
              ))}
              {modelos.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <Vazio>Nenhuma chamada de IA reportada neste período.</Vazio>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <Titulo sub="O que o registro publicado manda usar. Os produtos leem isto no boot.">
          Papéis no ar
        </Titulo>
        {papeis.length === 0 ? (
          <Card><Vazio>Nenhuma edição publicada. Os produtos estão na cópia compilada da lib.</Vazio></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {papeis.map(([nome, papel]) => (
              <Card key={nome} className="flex flex-col gap-2 p-4">
                <span className="font-mono text-sm font-bold text-pinho-600">{nome}</span>
                <span className="text-xs text-zinc-500">{papel.description}</span>
                <div className="flex flex-wrap items-center gap-1 font-mono text-[11px]">
                  {papel.cascade.map((m, i) => (
                    <span key={m} className="flex items-center gap-1">
                      {i > 0 && <span className="text-zinc-400">→</span>}
                      <span className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5">{m}</span>
                    </span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
